import type { TileId } from './tiles';

export type BiomeId =
  'backyard' | 'park' | 'farm' | 'golf' | 'cemetery' | 'estate' | 'stadium' | 'meadow';

export interface BiomeDef {
  id: BiomeId;
  name: string;
  /** Campaign levels in this biome (inclusive). */
  levels: readonly [number, number];
  /** Relative weights for scattered obstacles; only unlocked tiles are used. */
  scatterWeights: Partial<Record<TileId, number>>;
  /** Relative weights for terrain patches. */
  terrainWeights: Partial<Record<TileId, number>>;
  /**
   * What is new here, for the Next Zone page: up to three short lines (at most 26 characters,
   * biomes.test).
   */
  teaser: readonly string[];
}

/** docs/GDD.md §7. Backyard to the Golf Course are specified; later biomes are placeholders. */
export const BIOMES: readonly BiomeDef[] = [
  {
    id: 'backyard',
    name: 'Backyard',
    levels: [1, 20],
    scatterWeights: { rock: 4, birdBath: 2, tree: 3, shrub: 3, gnome: 2, sticks: 2 },
    terrainWeights: { flowerBed: 3, tallGrass: 4, mud: 2, water: 1 },
    teaser: ['Gnomes, bird baths, rocks', 'Tall grass and mud', 'Squirrels to shoo'],
  },
  {
    id: 'park',
    name: 'Neighborhood Park',
    levels: [21, 40],
    scatterWeights: { tree: 4, bench: 4, lampPost: 3, bin: 2, shrub: 2, rock: 1, sticks: 2 },
    terrainWeights: { flowerBed: 4, tallGrass: 3, mud: 2 },
    teaser: ['Hoses and sprinklers', 'Rabbits, and a dog', 'Benches, bins and lamps'],
  },
  {
    id: 'farm',
    name: 'Farm',
    levels: [41, 60],
    scatterWeights: { hayBale: 5, trough: 2, tree: 2, rock: 2, sticks: 3 },
    terrainWeights: { wheat: 4, crops: 4, mud: 4, tallGrass: 1 },
    teaser: ['Wheat and crop rows', 'Hay bales and troughs', 'Golden-hour light'],
  },
  {
    id: 'golf',
    name: 'Golf Course',
    levels: [61, 80],
    scatterWeights: { tree: 5, ballWasher: 2, shrub: 2, bench: 1, rock: 1, sticks: 1 },
    terrainWeights: { tallGrass: 4, flowerBed: 2, mud: 1 },
    teaser: ['Putting greens: keep off', 'Moles and molehills', 'Ball washers and flags'],
  },
  {
    id: 'cemetery',
    name: 'Old Cemetery Grounds',
    levels: [81, 100],
    // Headstones mostly stand in rows (the layout stage); a few strays, lanterns and shrubs.
    scatterWeights: {
      headstone: 2,
      lantern: 2,
      shrub: 3,
      tree: 2,
      rock: 1,
      sticks: 3,
      deadTree: 2,
    },
    terrainWeights: { tallGrass: 4, mud: 2, flowerBed: 1 },
    teaser: ['Thorns: mind your tires', 'Old trees that fall', 'Lanterns at dusk'],
  },
  {
    id: 'estate',
    name: 'Grand Estate',
    levels: [101, 120],
    // Formal gardens: everything comes in mirrored pairs (the generator's symmetric mode).
    scatterWeights: { topiary: 4, statue: 2, shrub: 2, tree: 2, birdBath: 1, sticks: 1 },
    terrainWeights: { flowerBed: 4, tallGrass: 2, mud: 1 },
    teaser: ['A river and footbridges', 'Hills: slow up, fast down', 'Topiary and statues'],
  },
  {
    id: 'stadium',
    name: 'Stadium',
    levels: [121, 140],
    // A pitch: cones from training, the odd bag of balls left out, worn patches.
    scatterWeights: { cone: 5, rock: 1, sticks: 1 },
    terrainWeights: { tallGrass: 3, mud: 2 },
    teaser: ['Stray balls: dodge them', 'Cones on the pitch', 'Under the floodlights'],
  },
  {
    id: 'meadow',
    name: 'Rolling Meadows',
    levels: [141, 160],
    // Open country: lone trees and bushes, rocks in the grass, fallen sticks, beehives (151 on);
    // wildflower meadow patches (they grow back if left half mowed), long grass, a muddy dip.
    scatterWeights: { tree: 3, shrub: 2, rock: 2, sticks: 2, beehive: 2 },
    terrainWeights: { meadowGrass: 6, tallGrass: 3, flowerBed: 1, mud: 1 },
    teaser: ['Wildflowers that regrow', 'Deer, foxes and a stag', 'Beehives and creeks'],
  },
];

/** The biome for a campaign level (levels past the last biome reuse the last one). */
export function biomeForLevel(level: number): BiomeDef {
  return (
    BIOMES.find((b) => level >= b.levels[0] && level <= b.levels[1]) ??
    (BIOMES[BIOMES.length - 1] as BiomeDef)
  );
}
